import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Button,
  List,
  ListItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  PlayCircle as PlayCircleIcon,
} from '@mui/icons-material';
import { useTherapistHistoryQuery } from '../../stores/tanstack/queries/useTherapistHistoryQuery';
import PatientForm from './PatientForm';

const PatientDetailDrawer = ({ patient, open, onClose }) => {
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Translation hook
  const { t } = useTranslation();

  // Fetch full patient data from API
  const { clients, isLoading, error, refetch } = useTherapistHistoryQuery();

  const isAnyLoading = isLoading || isUpdating;

  // Find the full patient details from the clients array
  const fullPatientDetails = useMemo(() => {
    if (!patient || !clients) return null;

    return clients.find(
      client => client.id === patient.id || client.userEmail === patient.email
    );
  }, [patient, clients]);

  if (!patient) return null;

  // Generate initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    return names.map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable', 'Not available');
    const date = new Date(dateString);
    return date.toLocaleDateString(t('dateLocale', 'de-DE'), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get status color and label
  const getStatusConfig = (status) => {
    switch (status) {
      case 'NEW':
        return { color: '#3B82F6', bg: '#DBEAFE', label: t('new', 'New') };
      case 'ACTIVE':
        return { color: '#10B981', bg: '#D1FAE5', label: t('active', 'Active') };
      case 'INACTIVE':
        return { color: '#6B7280', bg: '#F3F4F6', label: t('inactive', 'Inactive') };
      default:
        return { color: '#6B7280', bg: '#F3F4F6', label: status || t('unknown', 'Unknown') };
    }
  };

  const statusConfig = getStatusConfig(fullPatientDetails?.status);

  const handleEditClick = () => setEditFormOpen(true);
  const handleEditFormClose = () => setEditFormOpen(false);
  const handleEditFormSubmit = async () => {
    setEditFormOpen(false);
    setIsUpdating(true);
    try {
      await refetch();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 450,
            maxWidth: '100%',
            background: '#3d4a5c',
            color: 'white',
          },
        }}
      >
        {isAnyLoading ? (
          <Box sx={{
            p: 3,
            textAlign: 'center',
            height: '100vh',  // Full height
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#3d4a5c'  // Match drawer bg
          }}>
            <CircularProgress sx={{ color: 'white', mb: 2 }} />
            <Typography>
              {isUpdating
                ? t('updatingPatientDetails')
                : t('loadingPatientDetails')
              }
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header with Close Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('patientDetails', 'Patient Details')}
              </Typography>
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress sx={{ color: 'white', mb: 2 }} />
                <Typography>{t('loadingPatientDetails', 'Loading patient details...')}</Typography>
              </Box>
            )}

            {/* Error State */}
            {error && (
              <Box sx={{ p: 3 }}>
                <Alert severity="error">
                  {t('failedToLoadPatientDetails', 'Failed to load patient details')}: {error.message}
                </Alert>
              </Box>
            )}

            {/* Content - Only show when data is loaded */}
            {!isLoading && !error && fullPatientDetails && (
              <>
                {/* Avatar and Basic Info Section */}
                <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      margin: '0 auto',
                      fontSize: '2.5rem',
                      fontWeight: 600,
                      bgcolor: '#4a5a6f',
                      mb: 2,
                    }}
                  >
                    {getInitials(`${fullPatientDetails.firstName} ${fullPatientDetails.lastName}`)}
                  </Avatar>

                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {fullPatientDetails.firstName} {fullPatientDetails.lastName}
                  </Typography>

                  {/* Status Badge */}
                  <Chip
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      background: statusConfig.bg,
                      color: statusConfig.color,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      mb: 1,
                    }}
                  />

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                    {fullPatientDetails.userEmail}
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Content Section */}
                <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
                  {/* Email Verified Status */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        mb: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      {t('emailVerified', 'Email Verified')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {fullPatientDetails.emailVerified ? (
                        <>
                          <CheckCircleIcon sx={{ color: '#10B981', fontSize: '1.25rem' }} />
                          <Typography sx={{ fontWeight: 500 }}>{t('yes', 'Yes')}</Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ color: '#EF4444', fontSize: '1.25rem' }} />
                          <Typography sx={{ fontWeight: 500 }}>{t('no', 'No')}</Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Registered On */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        mb: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      {t('registeredOn', 'Registered On')}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {formatDate(fullPatientDetails.registeredOn)}
                    </Typography>
                  </Box>

                  {/* Last Practice */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        mb: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      {t('lastPractice', 'Last Practice')}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {formatDate(fullPatientDetails.lastPracticeDate)}
                    </Typography>
                  </Box>

                  {/* Onboarding Status */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        mb: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      {t('onboardingStatus', 'Onboarding Status')}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {fullPatientDetails.onboardingCompletedAt
                        ? `${t('completedOn', 'Completed on')} ${formatDate(fullPatientDetails.onboardingCompletedAt)}`
                        : t('notCompleted', 'Not completed')
                      }
                    </Typography>
                  </Box>

                  {/* Treatment Duration */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        mb: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      {t('treatmentDuration', 'Treatment Duration')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {fullPatientDetails.numberOfWeeks > 0 && (
                        <Chip
                          label={`${fullPatientDetails.numberOfWeeks} ${t('weeks', 'Weeks')}`}
                          size="small"
                          sx={{
                            bgcolor: '#4a5a6f',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      )}
                      {fullPatientDetails.numberOfMonths > 0 && (
                        <Chip
                          label={`${fullPatientDetails.numberOfMonths} ${t('months', 'Months')}`}
                          size="small"
                          sx={{
                            bgcolor: '#4a5a6f',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      )}
                      {fullPatientDetails.numberOfDays > 0 && (
                        <Chip
                          label={`${fullPatientDetails.numberOfDays} ${t('days', 'Days')}`}
                          size="small"
                          sx={{
                            bgcolor: '#4a5a6f',
                            color: 'white',
                            fontWeight: 500,
                          }}
                        />
                      )}
                      {fullPatientDetails.numberOfWeeks === 0 &&
                        fullPatientDetails.numberOfMonths === 0 &&
                        fullPatientDetails.numberOfDays === 0 && (
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            {t('notSet', 'Not set')}
                          </Typography>
                        )}
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 3 }} />

                  {/* Assigned Videos */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        mb: 1.5,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {t('assignedVideos', 'Assigned Videos')} ({fullPatientDetails.videos?.length || 0})
                    </Typography>

                    {fullPatientDetails.videos && fullPatientDetails.videos.length > 0 ? (
                      <List sx={{ p: 0 }}>
                        {fullPatientDetails.videos.map((video) => (
                          <ListItem
                            key={video.id}
                            sx={{
                              bgcolor: '#4a5a6f',
                              borderRadius: 2,
                              mb: 1.5,
                              px: 2,
                              py: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <PlayCircleIcon sx={{ color: '#10B981', fontSize: '1.25rem' }} />
                              <Typography sx={{ fontWeight: 600, flex: 1 }}>
                                {video.title}
                              </Typography>
                            </Box>

                            {video.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {video.description}
                              </Typography>
                            )}

                            <Chip
                              label={`${t('videoId', 'ID')}: ${video.externalId}`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                bgcolor: '#3d4a5c',
                                color: 'rgba(255, 255, 255, 0.8)',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 3,
                          bgcolor: '#4a5a6f',
                          borderRadius: 2
                        }}
                      >
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {t('noVideosAssigned', 'No videos assigned yet')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Edit Button */}
                <Box sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                    sx={{
                      bgcolor: '#2563EB',
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: '#1D4ED8',
                      },
                    }}
                  >
                    {t('editPatient', 'Edit Patient')}
                  </Button>
                </Box>
              </>
            )}

            {/* Fallback if patient not found in API data */}
            {!isLoading && !error && !fullPatientDetails && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('patientDetailsNotFound', 'Patient details not found')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

      </Drawer>

      {/* Edit Form Dialog */}
      <PatientForm
        open={editFormOpen}
        onClose={handleEditFormClose}
        onSubmit={handleEditFormSubmit}
        editPatient={fullPatientDetails}
        onPatientAdded={refetch}
      />
    </>
  );
};

export default PatientDetailDrawer;